'use client';
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useToast } from '@/hooks/useToast';
import Loader from '@/components/ui/Loader';
import { MARKETING_PUBLIC_PATHS } from '@/constants/publicRoutes';

// Auth flow tracing — development only. Route names, session state and
// redirect decisions must never print in a production console.
const debugLog = process.env.NODE_ENV !== 'production' ? console.log : () => {};


// Marketing routes come from the shared list so this guard can't drift
// out of sync with ConditionalLayout and the sitemap — a path missing
// here falls through to the auth-checking branch below and returns
// <Loader fullScreen />, so the server-rendered HTML is an empty spinner
// and Google indexes nothing.
const PUBLIC_PATHS = [
  ...MARKETING_PUBLIC_PATHS,
  '/auth/sign-in',
  '/auth/superadmin-login',
  '/auth/verify-otp',
  '/auth/reset-password',
  '/auth/register-school',
  // Pay Later payment page — no account exists until it is paid.
  '/onboarding/pay',
  '/domain-not-found',
];

// School-theme sub-routes served through the iframe on a school domain.
// Must stay in sync with `THEME_PUBLIC_PATHS` in middleware.ts — any
// deep-link path the theme's navbar exposes needs to be public here
// too, or Ctrl+click to pages like /admission-process bounces logged-out
// visitors to sign-in.
const THEME_PUBLIC_PATHS = [
  '/academics',
  '/admission-process',
  '/apply',
  '/blog',
  '/blogs',
  '/contact',
  '/enquiry',
  '/gallery',
  '/notices',
  '/privacy',
  '/results',
  '/rules',
  '/syllabus',
  '/terms',
];
const isThemePublicPath = (p) => {
  if (!p) return false;
  return THEME_PUBLIC_PATHS.some((base) => p === base || p.startsWith(`${base}/`));
};
import { matchAdminPathSection } from '@/utils/permissionPaths';
import { matchSuperAdminPathRule, saRuleAllows } from '@/utils/superAdminPaths';
import { signInPathForRole } from '@/utils/auth';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

export default function AuthGuard({ children }) {
  const router = useRouter();
  const pathname = usePathname();
  const { showToast } = useToast();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Check for token in cookie
      const getCookie = (name) => {
        if (typeof document === 'undefined') return null;
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      const token = getCookie('kayease_token');
      const hasSessionToken = typeof window !== 'undefined' && sessionStorage.getItem('authToken');
      let userRole = typeof window !== 'undefined' ? sessionStorage.getItem('userRole') : null;
      const isDomainNotFound = pathname === '/domain-not-found' || pathname?.startsWith('/domain-not-found');
      const isPublicPath =
        pathname === '/' ||
        pathname === '' ||
        isDomainNotFound ||
        PUBLIC_PATHS.some((path) => path !== '/' && pathname?.startsWith(path)) ||
        isThemePublicPath(pathname);

      debugLog('🛡️ [AuthGuard] Path checking:', { pathname, isPublicPath, isDomainNotFound });
      // CROSS-TAB LOGOUT SYNC:
      // If we have sessionStorage data but NO cookie, it means the user logged out in another tab.
      // We must clear the local session to prevent access.
      if (hasSessionToken && !token) {
        sessionStorage.clear();
        // Force reload to state variables
        userRole = null;
      }

      const isAuthenticated = !!token; // Strictly rely on cookie for validity
      debugLog('🛡️ [AuthGuard] Checking session...', { isAuthenticated, userRole, isPublicPath });

      // If we have a token but no role (e.g. after browser restart where sessionStorage was cleared),
      // fetch the current user to restore the role into sessionStorage so menus and guards work.
      if (isAuthenticated && !userRole && API_BASE_URL) {
        try {
          const fetchUrl = `${API_BASE_URL}/api/users/me`;
          debugLog('🛡️ [AuthGuard] Restoring session from:', fetchUrl);

          const response = await fetch(fetchUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });

          debugLog('🛡️ [AuthGuard] Me Response Status:', response.status);

          if (response.ok) {
            const data = await response.json().catch(() => ({}));
            const fetchedRole = data.user?.role;
            debugLog('✅ [AuthGuard] Session restored. Role:', fetchedRole);
            if (fetchedRole && typeof window !== 'undefined') {
              sessionStorage.setItem('userRole', fetchedRole);
              sessionStorage.setItem('authToken', token);
              userRole = fetchedRole;
            }
          } else {
            console.warn('⚠️ [AuthGuard] Session restore failed (Unauthorized or Server Error)');
          }
        } catch (err) {
          console.error('❌ [AuthGuard] Session restore connection error:', err);
        }
      }

      const isAuthPath = pathname && (pathname.startsWith('/auth') || pathname.startsWith('/login'));

      // If user is authenticated and trying to access auth pages, redirect to dashboard
      if (isAuthPath && isAuthenticated) {
        debugLog('🔒 [AuthGuard] Authenticated user on public path, redirecting...', {
          userRole,
          isPublicPath,
          pathname
        });

        const roleDashboardMap = {
          superadmin: '/superadmin-dashboard',
          schooladmin: '/admin-dashboard',
          teacher: '/teacher-dashboard',
          student: '/student-dashboard',
        };
        const dashboard = roleDashboardMap[userRole] || '/staff-dashboard';

        debugLog('🎯 [AuthGuard] Dashboard redirect:', {
          userRole,
          mappedDashboard: roleDashboardMap[userRole],
          finalDashboard: dashboard
        });

        router.replace(dashboard);
        return;
      }

      // If not a public path and no authentication, redirect to sign-in
      if (!isPublicPath && !isAuthenticated) {
        const params = new URLSearchParams();
        if (pathname && pathname !== '/') {
          params.append('redirect', pathname);
        }
        params.append('error', 'session_expired');
        // A superadmin signs in at their own form — bouncing an expired
        // superadmin session to the SCHOOL form is a dead end, since it
        // asks for a school code they don't have. `userRole` is still in
        // sessionStorage here unless another tab already cleared it, in
        // which case the school form is the right default anyway.
        router.push(`${signInPathForRole(userRole)}?${params.toString()}`);
        return;
      }

      // Role-based access control
      if (!isPublicPath && userRole) {
        const roleDashboardMap = {
          superadmin: '/superadmin-dashboard',
          schooladmin: '/admin-dashboard',
          teacher: '/teacher-dashboard',
          student: '/student-dashboard',
        };

        // Super Admin only routes
        if (
          (pathname.startsWith('/superadmin/') || pathname.startsWith('/superadmin-dashboard')) &&
          userRole !== 'superadmin'
        ) {
          debugLog('⛔ [AuthGuard] Access denied - Super Admin only route', {
            pathname,
            userRole,
            redirectingTo: roleDashboardMap[userRole] || '/staff-dashboard'
          });

          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // SuperAdmin SECTION gate — hiding a sidebar entry is not access
        // control. A sub-user who typed `/superadmin/reports` used to get
        // the page shell and only discovered the block when a data call
        // or an export came back 403. Bounce the navigation instead, so
        // they land on their dashboard with a clear message.
        // Fail-OPEN on a lookup error: the backend re-checks every request
        // with requireSASection/requireOwner, so a network blip must not
        // lock a legitimate owner out of their own portal.
        // Not restricted to /superadmin/* — Help Desk lives at /helpdesk and
        // is shared with schooladmins, so the map decides. It returns null for
        // anything unmapped, and the fetch below only runs on a match.
        if (userRole === 'superadmin') {
          const saRule = matchSuperAdminPathRule(pathname);
          if (saRule && API_BASE_URL) {
            let saPerms = null;
            try {
              const saRes = await fetch(`${API_BASE_URL}/api/super-admin/permissions/me`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (saRes.ok) saPerms = await saRes.json().catch(() => null);
            } catch (err) {
              console.error('[AuthGuard] SuperAdmin permission lookup failed:', err);
            }
            if (saPerms && !saRuleAllows(saRule, saPerms)) {
              debugLog('⛔ [AuthGuard] Access denied - SuperAdmin section', {
                pathname,
                rule: saRule,
              });
              router.replace('/superadmin-dashboard');
              showToast({
                message: saPerms.inactive
                  ? 'Your SuperAdmin account is inactive — contact an owner'
                  : saRule.ownerOnly
                    ? 'Only a SuperAdmin owner can open this page'
                    : 'You do not have permission to access this page',
                severity: 'error',
              });
              return;
            }
          }
        }

        // Notice, Events, Holidays — informational pages open to every
        // school user (including non-teaching staff whose role string is
        // designation-derived: 'Accountant', 'Librarian', etc.).
        // Only superadmin is bounced; their dashboard lives elsewhere.
        if (
          (pathname.startsWith('/notice') ||
            pathname.startsWith('/events') ||
            pathname.startsWith('/holidays')) &&
          userRole === 'superadmin'
        ) {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
            duration: 3000,
          });
          return;
        }

        // Admin only routes
        const isAdminOnlyPath =
          pathname.startsWith('/students') ||
          (pathname.startsWith('/staff/') || pathname === '/staff') ||  // Fixed: Don't match /staff-dashboard
          pathname.startsWith('/teachers') ||
          pathname.startsWith('/subject') ||
          (pathname.startsWith('/examinations') && !pathname.startsWith('/examinations/results') && !pathname.startsWith('/examinations/schedule')) ||
          pathname.startsWith('/promote') ||
          pathname.startsWith('/fees') ||
          pathname.startsWith('/library') ||
          pathname.startsWith('/sports') ||
          // /hostel is admin-side EXCEPT for students, who get their own
          // "My Hostel" self-service view (TC pattern).
          (pathname.startsWith('/hostel') && userRole !== 'student') ||
          // /transport is admin-side EXCEPT for students, who get their own
          // read-only "My Transport" view (bus/route/stop + live tracking).
          // Same shape as /hostel above.
          (pathname.startsWith('/transport') && userRole !== 'student') ||
          pathname.startsWith('/staffs') ||
          pathname.startsWith('/departments') ||
          pathname.startsWith('/designation') ||
          (pathname.startsWith('/attendance') && !pathname.startsWith('/attendance-report')) ||
          // NOTE: `/leaves` deliberately NOT listed here. The leaves
          // page is role-aware: schooladmin sees the approval queue
          // + their own apply history, every other non-student
          // staff role sees only their own apply + history view.
          // Gating it to schooladmin-only here would silently
          // redirect teachers/staff who click the sidebar entry
          // (which IS visible to them per Sidebar.jsx#hrm-leaves).
          pathname.startsWith('/payroll') ||
          // `/leave-report` deliberately NOT listed — page is role
          // aware: schooladmin sees the org-wide aggregate report,
          // every other non-student staff role sees a report of
          // their own leaves only. Sidebar visibility is already
          // gated on `features: ['hasHRM', 'hasReports']` so a school
          // without those subscriptions doesn't even see the entry.
          pathname.startsWith('/fees-report') ||
          pathname.startsWith('/permissions') ||
          pathname.startsWith('/settings') ||
          pathname.startsWith('/admin-dashboard') ||
          pathname.startsWith('/activity-logs') ||
          // Data Export — schooladmin by default; grant-unlockable via the
          // 'data-export' section (see ADMIN_PATH_SECTION_MAP). The edge
          // middleware lets teacher/staff through precisely so this grant
          // check can run.
          pathname.startsWith('/data-export') ||
          // Student Pickup — schooladmin by default; grant-unlockable via
          // the 'pickup' section. Students included on purpose (they hold
          // no school-wide grant, so they are bounced): parents use the app.
          pathname.startsWith('/pickup') ||
          pathname === '/classes/list';

        if (isAdminOnlyPath && userRole !== 'schooladmin') {
          // Section-grant override — if the schooladmin has granted
          // this user `read` on the section that owns this path,
          // let them in. Per-button CRUD inside the page is then
          // gated separately by the page's `usePermissions` hook,
          // and the backend re-checks every call.
          // Paths whose section is `null` (e.g. /permissions,
          // /admin-dashboard, /payroll, /promote, /attendance)
          // intentionally cannot be unlocked by a grant — they remain
          // schooladmin-only. (/hostel, /library, /sports, /pickup and
          // /transport ARE grant-unlockable via their sections — a
          // warden/librarian/coach/bus-crew or an involved
          // student/borrower reaches them through the implicit reveal
          // in /api/permissions/me.)
          const overrideSection = matchAdminPathSection(pathname);
          let hasOverride = false;
          if (overrideSection) {
            try {
              const permRes = await fetch(
                `${API_BASE_URL}/api/permissions/me`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              if (permRes.ok) {
                const permData = await permRes.json().catch(() => ({}));
                if (permData?.isSchoolAdmin) {
                  hasOverride = true;
                } else {
                  const grant = Array.isArray(permData?.sections)
                    ? permData.sections.find((s) => s.section === overrideSection)
                    : null;
                  if (grant?.read) hasOverride = true;
                }
              }
            } catch (err) {
              console.error('[AuthGuard] permission lookup failed:', err);
            }
          }

          if (!hasOverride) {
            debugLog('⛔ [AuthGuard] Access denied - Admin only route', {
              pathname,
              userRole,
              overrideSection,
              redirectingTo: roleDashboardMap[userRole] || '/staff-dashboard'
            });

            router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
            showToast({
              message: 'You do not have permission to access this page',
              severity: 'error',
            });
            return;
          }

          debugLog('✅ [AuthGuard] Access granted via section grant', {
            pathname,
            overrideSection,
          });
        }

        // Teacher only routes
        if (
          pathname.startsWith('/teacher-dashboard') &&
          userRole !== 'teacher'
        ) {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // Student only routes
        if (pathname.startsWith('/student-dashboard') && userRole !== 'student') {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // Transfer Certificates — schooladmin + student by role; any
        // other role needs a 'transfer-certificates' section READ grant
        // (same override-fetch pattern as the isAdminOnlyPath block —
        // the backend re-checks with requireSection either way). This
        // page is deliberately NOT in ADMIN_PATH_SECTION_MAP: that
        // branch bounces every ungranted non-admin, which would lock
        // out students from their own self-service view.
        if (
          pathname.startsWith('/transfer-certificates') &&
          !['schooladmin', 'student'].includes(userRole)
        ) {
          let hasTCGrant = false;
          try {
            const permRes = await fetch(`${API_BASE_URL}/api/permissions/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (permRes.ok) {
              const permData = await permRes.json().catch(() => ({}));
              const grant = Array.isArray(permData?.sections)
                ? permData.sections.find((s) => s.section === 'transfer-certificates')
                : null;
              if (grant?.read) hasTCGrant = true;
            }
          } catch (err) {
            console.error('[AuthGuard] TC permission lookup failed:', err);
          }
          if (!hasTCGrant) {
            router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
            showToast({
              message: 'You do not have permission to access this page',
              severity: 'error',
            });
            return;
          }
        }

        // Report Cards — schooladmin/teacher/student by role; any other
        // role needs a 'report-cards' section READ grant (same pattern
        // as Transfer Certificates above — the backend re-checks with
        // requireSection either way).
        if (
          pathname.startsWith('/report-cards') &&
          !['schooladmin', 'teacher', 'student'].includes(userRole)
        ) {
          let hasRCGrant = false;
          try {
            const permRes = await fetch(`${API_BASE_URL}/api/permissions/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (permRes.ok) {
              const permData = await permRes.json().catch(() => ({}));
              const grant = Array.isArray(permData?.sections)
                ? permData.sections.find((s) => s.section === 'report-cards')
                : null;
              if (grant?.read) hasRCGrant = true;
            }
          } catch (err) {
            console.error('[AuthGuard] Report card permission lookup failed:', err);
          }
          if (!hasRCGrant) {
            router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
            showToast({
              message: 'You do not have permission to access this page',
              severity: 'error',
            });
            return;
          }
        }

        // School Admin, Teacher and Student routes
        if (
          (pathname === '/classes/homework' ||
            pathname === '/classes/assignments' ||
            pathname === '/classes/class-test' ||
            pathname === '/classes/consent-form') &&
          !['schooladmin', 'teacher', 'student'].includes(userRole)
        ) {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // Attendance report - Admin, Teacher and Student only
        if (pathname.startsWith('/attendance-report') && !['schooladmin', 'teacher', 'student'].includes(userRole)) {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // Class report and Student report - Admin and Teacher only
        if (
          (pathname.startsWith('/class-report') || pathname.startsWith('/student-report')) &&
          !['schooladmin', 'teacher'].includes(userRole)
        ) {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // Exam results and Exam Schedule - Admin, Teacher, and Student
        if ((pathname.startsWith('/examinations/results') || pathname.startsWith('/examinations/schedule')) && !['schooladmin', 'teacher', 'student'].includes(userRole)) {
          router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
          showToast({
            message: 'You do not have permission to access this page',
            severity: 'error',
          });
          return;
        }

        // Feature-based access control
        // Values can be a single string or an array of strings (ALL must be enabled).
        const FEATURE_PATHS = {
          '/library':           'hasLibrary',
          '/hostel':            'hasHostel',
          '/transport':         'hasTransport',
          '/sports':            'hasSports',
          '/messages':          'hasInAppMessaging',
          '/helpdesk':          'hasHelpdesk',
          '/attendance-report': 'hasReports',
          '/class-report':      'hasReports',
          '/student-report':    'hasReports',
          '/leave-report':      ['hasHRM', 'hasReports'], // requires HRM & Payroll AND Advanced Reports
          '/fees-report':       'hasReports',
          '/report-cards':      'hasReports',
          '/attendance':        'hasHRM',
          '/leaves':            'hasHRM',
          '/payroll':           'hasHRM',
        };

        const matchingPath = Object.keys(FEATURE_PATHS).find(path => pathname.startsWith(path));
        if (matchingPath && userRole !== 'superadmin') {
          try {
            const res = await fetch(`${API_BASE_URL}/api/schools/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              const required = FEATURE_PATHS[matchingPath];
              const flags    = Array.isArray(required) ? required : [required];
              const hasAll   = flags.every(f => data.school?.subscription?.[f]);
              if (!hasAll) {
                debugLog(`⛔ [AuthGuard] Feature denied: ${flags.join(', ')} for ${pathname}`);
                router.replace(roleDashboardMap[userRole] || '/staff-dashboard');
                showToast({
                  message: 'This feature is not included in your current subscription plan.',
                  severity: 'error',
                });
                return;
              }
            }
          } catch (err) {
            console.error('Feature check failed', err);
          }
        }
      }
      setIsChecking(false);
    };

    // Handle bfcache (back/forward cache)
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsChecking(true);
        checkAuth();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    checkAuth();

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [pathname, router, showToast]);

  // Show loading state while checking authentication.
  // The landing root is fully public and has its own Preloader, so we let
  // the page render immediately and run auth checks in the background —
  // otherwise the generic Loader flashes before the landing Preloader can
  // mount.
  const isLandingPublic =
    pathname === '/' ||
    pathname === '' ||
    PUBLIC_PATHS.some((path) => path !== '/' && pathname?.startsWith(path));
  if (isChecking && !isLandingPublic) {
    return <Loader fullScreen />;
  }

  return <>{children}</>;
}


